import React, { useState, useEffect } from 'react';
import { CheckCircle2, X, Plus, Edit3, Trash2 } from 'lucide-react';
import DashboardShell from '@/components/layout/DashboardShell';
import { useAuth, formatApiError } from '@/lib/auth';
import api from '@/lib/api';
import { MODULES } from '@/lib/modules';

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState('plans');
  return (
    <DashboardShell sectionTitle="Settings">
      <div className="mb-6">
        <h1 className="font-display font-bold text-3xl tracking-tight text-navy">Settings</h1>
        <p className="text-neutral-600 mt-1 text-sm">Configure subscription plans, branding, and platform options.</p>
      </div>
      <div className="flex items-center gap-2 mb-6 border-b border-neutral-200">
        {[
          { k: 'plans', l: 'Subscription plans' },
          { k: 'branding', l: 'Branding' },
          { k: 'security', l: 'Security' },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.k ? 'border-amber text-navy' : 'border-transparent text-neutral-500 hover:text-navy'}`} data-testid={`settings-tab-${t.k}`}>{t.l}</button>
        ))}
      </div>
      {tab === 'plans' && <PlansTab user={user} />}
      {tab === 'branding' && <div className="bg-white p-6 rounded-md border border-neutral-200 text-sm text-neutral-600">Branding settings (logo, colors, custom domain) coming soon. The platform default branding is applied for now.</div>}
      {tab === 'security' && <div className="bg-white p-6 rounded-md border border-neutral-200 text-sm text-neutral-600 space-y-2">
        <div><strong className="text-navy">Password policy:</strong> Min 8 characters · admin-driven reset · forced change on first login.</div>
        <div><strong className="text-navy">Brute-force protection:</strong> 5 failed attempts → 15-min lockout per email.</div>
        <div><strong className="text-navy">Multi-tenant isolation:</strong> Org-scoped data via server-side tenant filter.</div>
        <div><strong className="text-navy">MFA TOTP:</strong> Coming soon.</div>
      </div>}
    </DashboardShell>
  );
}

function PlansTab({ user }) {
  const [plans, setPlans] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const isSuper = user?.role === 'super_admin';

  const load = async () => {
    try { const { data } = await api.get('/plans'); setPlans(data.items || []); } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    try { await api.delete(`/plans/${id}`); load(); } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-neutral-600">Plans control what modules and tools an organization can access. Tools list is empty = all tools.</p>
        {isSuper && <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-accent text-sm" data-testid="new-plan-btn"><Plus className="w-4 h-4" /> New plan</button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="bg-white p-6 border border-neutral-200 rounded-md hover:border-amber/40 transition-colors relative">
            {isSuper && (
              <div className="absolute top-3 right-3 flex gap-1">
                <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-1.5 rounded hover:bg-neutral-100 text-neutral-500"><Edit3 className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(p.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )}
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber/10 text-amber-700 border border-amber-200">{p.tier}</span>
            <div className="font-display font-bold text-2xl text-navy mt-3">{p.name}</div>
            <div className="font-display font-bold text-3xl text-amber mt-1">₦{p.monthly_price.toLocaleString()}<span className="text-sm font-normal text-neutral-500">/mo</span></div>
            <p className="text-sm text-neutral-600 mt-2 mb-4">{p.description}</p>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">Modules ({p.modules.length})</div>
            <div className="flex flex-wrap gap-1 mb-3">
              {p.modules.slice(0, 6).map((slug) => {
                const m = MODULES.find((x) => x.slug === slug);
                return m ? <span key={slug} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${m.accent}15`, color: m.accent }}>{m.short}</span> : null;
              })}
              {p.modules.length > 6 && <span className="text-[10px] text-neutral-500">+{p.modules.length - 6}</span>}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">Features</div>
            <ul className="space-y-1">
              {(p.features || []).map((f, i) => (
                <li key={i} className="text-xs text-neutral-700 flex items-start gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {showForm && <PlanForm plan={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function PlanForm({ plan, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: plan?.name || '', tier: plan?.tier || 'starter',
    monthly_price: plan?.monthly_price || 0, yearly_price: plan?.yearly_price || 0,
    description: plan?.description || '', max_users: plan?.max_users || 25,
    modules: plan?.modules || [], tools: plan?.tools || [], features: plan?.features || [],
  }));
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const toggleMod = (s) => setForm((f) => ({ ...f, modules: f.modules.includes(s) ? f.modules.filter((x) => x !== s) : [...f.modules, s] }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      if (plan?.id) await api.patch(`/plans/${plan.id}`, form);
      else await api.post('/plans', form);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
          <h3 className="font-display font-semibold text-xl text-navy">{plan ? 'Edit plan' : 'New plan'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Plan name" v={form.name} on={(v) => setForm({ ...form, name: v })} req />
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">Tier</span>
              <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none bg-white">
                <option value="starter">Starter</option><option value="growth">Growth</option><option value="enterprise">Enterprise</option><option value="custom">Custom</option>
              </select>
            </label>
            <Inp label="Monthly price (₦)" v={form.monthly_price} on={(v) => setForm({ ...form, monthly_price: Number(v) })} type="number" />
            <Inp label="Yearly price (₦)" v={form.yearly_price} on={(v) => setForm({ ...form, yearly_price: Number(v) })} type="number" />
            <Inp label="Max users" v={form.max_users} on={(v) => setForm({ ...form, max_users: Number(v) })} type="number" />
          </div>
          <Inp label="Description" v={form.description} on={(v) => setForm({ ...form, description: v })} />
          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-2">Modules included</div>
            <div className="grid grid-cols-3 gap-1.5 max-h-44 overflow-y-auto p-2 bg-neutral-50 rounded">
              {MODULES.map((m) => (
                <label key={m.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs ${form.modules.includes(m.slug) ? 'bg-white border border-navy' : 'hover:bg-white'}`}>
                  <input type="checkbox" checked={form.modules.includes(m.slug)} onChange={() => toggleMod(m.slug)} />
                  <span className="truncate">{m.short}</span>
                </label>
              ))}
            </div>
          </div>
          {err && <div className="p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-700">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline text-sm">Cancel</button>
            <button type="submit" disabled={busy} className="btn-accent text-sm" data-testid="submit-plan">{busy ? 'Saving…' : 'Save plan'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Inp({ label, v, on, type = 'text', req }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">{label}</span>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} required={req} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-navy" />
    </label>
  );
}
